/**
 * アプリケーションのルートコンポーネント。
 *
 * - 認証状態に応じて Login / SignUp / メインページを切り替える。
 * - 未認証時は `/signup` でセルフサインアップが可能、それ以外は `/login` に集約される。
 * - 認証済みの場合は `/login` `/signup` を踏んでもメインページにリダイレクトする。
 */
import { Navigate, Route, Routes, useNavigate } from 'react-router-dom';

import { useAuth } from './auth/AuthProvider';
import { LoginPage } from './auth/LoginPage';
import { ResetPasswordPage } from './auth/ResetPasswordPage';
import { SignUpPage } from './auth/SignUpPage';
import type { AppConfig } from './config';
import { BroadcastPointsPage } from './pages/BroadcastPointsPage';

interface AppProps {
  config: AppConfig;
}

export default function App({ config }: AppProps): React.JSX.Element {
  const { isAuthenticated, isLoading } = useAuth();

  if (isLoading) {
    return (
      <div style={{ padding: 24, textAlign: 'center', color: '#6b7280' }}>
        セッションを確認中...
      </div>
    );
  }

  if (!isAuthenticated) {
    // 未認証: ログイン / サインアップのみ表示する
    return (
      <Routes>
        <Route path="/login" element={<LoginPage />} />
        <Route path="/signup" element={<SignUpPage />} />
        <Route path="/reset-password" element={<ResetPasswordPage />} />
        <Route path="*" element={<Navigate to="/login" replace />} />
      </Routes>
    );
  }

  return (
    <>
      <AppHeader />
      <main className="app-main">
        <Routes>
          <Route path="/" element={<BroadcastPointsPage config={config} />} />
          {/* 認証済みでログイン/サインアップへ来た場合はトップへリダイレクト */}
          <Route path="/login" element={<Navigate to="/" replace />} />
          <Route path="/signup" element={<Navigate to="/" replace />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </main>
    </>
  );
}

function AppHeader(): React.JSX.Element {
  const { username, signOut } = useAuth();
  const navigate = useNavigate();
  return (
    <header className="app-header">
      <h1>OBA-Core 放送地点マスタ</h1>
      <div className="user-info">
        <span>{username ?? ''}</span>
        <button
          type="button"
          onClick={() => {
            signOut();
            navigate('/');
          }}
        >
          ログアウト
        </button>
      </div>
    </header>
  );
}
